<script>
import { ref, watch } from 'vue';
import { mapGetters } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import { needsConversationShowFetch } from './conversationPayload';
import { fetchConversationLookups } from './conversationLookups';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import { useWorkbenchLayout } from 'dashboard/composables/useWorkbenchLayout';
import { useFocusConversationQueue } from 'dashboard/composables/useFocusConversationQueue';
import { useMobileConversationUi } from 'dashboard/composables/useMobileConversationUi';
import FocusQueueBar from 'dashboard/components-next/Conversation/FocusQueue/FocusQueueBar.vue';
import FocusQueueEmpty from 'dashboard/components-next/Conversation/FocusQueue/FocusQueueEmpty.vue';
import WorkspaceContactToggle from 'dashboard/components-next/workspaceV2/WorkspaceContactToggle.vue';
import WorkspaceResizeControl from 'dashboard/components-next/workspaceV2/WorkspaceResizeControl.vue';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationSidebar,
    FocusQueueBar,
    FocusQueueEmpty,
    WorkspaceResizeControl,
    WorkspaceContactToggle,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { accountId } = useAccount();
    const {
      isListHidden,
      isAvatarRail,
      isOnSmallScreen,
      toggleListCompact,
      startListDrag,
      startRightDrag,
      listDragWidth,
      listWidthPx,
      isListDragging,
      listDragPreviewPx,
      listSnapTarget,
      listSnapGuides,
      listResizeWidth,
      listAvailableWidth,
      setListResizeWidth,
      hideConversationList,
      cancelListDrag,
    } = useWorkbenchLayout();
    const { isWorkspaceV2 } = useWorkspaceV2();
    const { isQueueEmpty } = useFocusConversationQueue({ autopilot: true });
    const { resetMobileContact } = useMobileConversationUi();

    // Origem das guias do arrasto: a borda esquerda da própria lista. Medida
    // uma vez por gesto (não a cada pointermove, que é o que causa layout).
    const listRailRef = ref(null);
    const listRailLeft = ref(0);
    watch(isListDragging, arrastando => {
      if (arrastando) {
        listRailLeft.value = Math.round(
          listRailRef.value?.getBoundingClientRect().left || 0
        );
      }
    });

    return {
      accountId,
      isListHidden,
      isAvatarRail,
      isOnSmallScreen,
      isQueueEmpty,
      toggleListCompact,
      startListDrag,
      startRightDrag,
      listDragWidth,
      listWidthPx,
      isListDragging,
      listDragPreviewPx,
      listSnapTarget,
      listSnapGuides,
      listResizeWidth,
      listAvailableWidth,
      setListResizeWidth,
      hideConversationList,
      cancelListDrag,
      listRailRef,
      listRailLeft,
      resetMobileContact,
      isWorkspaceV2,
    };
  },
  data() {
    return {
      showSearchModal: false,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      return !this.isOnSmallScreen || !this.conversationId;
    },
    isListRailClosed() {
      return (
        this.isListHidden && !this.isOnSmallScreen && this.listDragWidth == null
      );
    },
    // Largura efetiva do rail. Em tela pequena listWidthPx já vem null e o
    // rail vai `w-full` (a lista é a janela). Fechado, quem manda é o
    // grid-template-columns do CSS.
    railStyle() {
      if (this.isOnSmallScreen || this.isListRailClosed) return undefined;
      // O rail de avatares tem 64px EXPLÍCITOS, nunca a largura salva do modo
      // lista (340 deixava vazio enorme ao lado) e nunca undefined: sem
      // largura, o grid auto-dimensionado colapsa no min-content do avatar
      // (42px, medido na v847) e esmaga o w-16 do filho.
      if (this.isAvatarRail) return { width: '64px' };
      if (this.listWidthPx == null) return undefined;
      return { width: `${this.listWidthPx}px` };
    },
    showMessageView() {
      return !this.isOnSmallScreen || Boolean(this.conversationId);
    },
    // SiteUp: no celular a conversa abre na conversa, nao no painel de contato.
    // isOnSmallScreen vem do useWorkbenchLayout (a lista tambem depende dele
    // para ocupar a janela inteira), entao nao ha copia local do breakpoint.
    showContactPanelWhenOpening() {
      return !this.isOnSmallScreen;
    },

    // SiteUp E4.1: o painel de contato agora anima o próprio colapso (ver
    // ConversationSidebar.vue) — por isso este v-if só cobre "existe
    // conversa pra mostrar", não "está aberto". Montar sempre que houver
    // conversa é o que permite a transição rodar em vez da DOM sumir na
    // hora do clique.
    hasCurrentChat() {
      return Boolean(this.currentChat.id);
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
      // SiteUp: no celular cada conversa nova comeca na conversa, nunca no painel.
      if (this.isOnSmallScreen) this.resetMobileContact();
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    fetchConversationLookups(this.$store);
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      // SiteUp: a lista traz o payload enxuto (atributos da conversa vazios, sem
      // follow-up nem assistente da IA). Ao abrir uma conversa listada buscamos o
      // SHOW até o payload completo chegar (ver conversationPayload.js).
      if (needsConversationShowFetch(chat)) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
  },
};
</script>

<template>
  <section class="flex flex-col w-full h-full min-w-0">
    <FocusQueueBar v-if="isListHidden" />
    <FocusQueueEmpty v-if="isQueueEmpty" />
    <section v-show="!isQueueEmpty" class="flex w-full min-h-0 min-w-0 flex-1">
      <!-- A largura vive AQUI, no rail, não na lista lá dentro. O rail é um
           grid (para animar o colapso em grid-template-columns) e o track dele
           limita o filho: com a largura só no ChatList, pedir 256px enquanto o
           rail resolvia 64px não mudava nada na tela — os encaixes "não
           ficavam". A lista interna ocupa 100% do que o rail der. -->
      <div
        ref="listRailRef"
        class="workbench-list-rail relative h-full min-h-0"
        :class="{
          'workbench-list-rail--closed': isListRailClosed,
          'workbench-list-rail--dragging': isListDragging,
          // SiteUp: no celular o rail é shrink-0 sem largura, então o grid
          // resolve no max-content (~319px numa janela de 375) e sobra faixa
          // morta. w-full só com a lista visível: com conversa aberta o rail
          // precisa colapsar (o ChatList vai `hidden`).
          'w-full min-w-0 flex-1': isOnSmallScreen && showConversationList,
          'shrink-0': !(isOnSmallScreen && showConversationList),
        }"
        :style="railStyle"
      >
        <div class="workbench-list-rail__inner">
          <ChatList
            :show-conversation-list="showConversationList"
            :conversation-inbox="inboxId"
            :label="label"
            :team-id="teamId"
            :conversation-type="conversationType"
            :folders-id="foldersId"
            :is-on-expanded-layout="isOnSmallScreen"
            @conversation-load="onConversationLoad"
          />
        </div>
      </div>
      <WorkspaceResizeControl
        v-if="isWorkspaceV2 && !isOnSmallScreen"
        :model-value="listResizeWidth"
        :max-width="listAvailableWidth"
        :is-hidden="isListHidden"
        :is-dragging="isListDragging"
        @start-drag="startListDrag"
        @cancel-drag="cancelListDrag"
        @select-width="setListResizeWidth"
        @hide="hideConversationList"
      />
      <div
        v-else-if="!isOnSmallScreen"
        data-testid="conversation-workbench-list-gutter"
        class="hidden md:flex relative w-2 shrink-0 h-full cursor-col-resize group items-center justify-center"
        @mousedown="startListDrag"
        @touchstart="startListDrag"
        @dblclick="toggleListCompact"
      >
        <div
          class="absolute inset-y-0 w-px bg-transparent group-hover:bg-n-brand transition-colors"
        />
      </div>
      <!-- Guias e indicador do arrasto. O deslocamento parte da BORDA DA LISTA,
           não da viewport: hoje a lista começa em x=0 no Modo Foco, mas se um
           dia voltar a existir algo à esquerda dela, `fixed inset-0` faria as
           guias nascerem deslocadas exatamente por essa largura. -->
      <div
        v-if="isListDragging"
        class="pointer-events-none fixed inset-y-0 right-0 z-[50]"
        data-testid="workbench-list-drag-overlay"
        :style="{ left: `${listRailLeft}px` }"
      >
        <div
          v-for="px in listSnapGuides"
          :key="px"
          class="absolute top-0 h-full w-px will-change-transform"
          :class="listSnapTarget === px ? 'bg-n-iris-9' : 'bg-n-weak'"
          :data-snap="px"
          :style="{ transform: `translate3d(${px}px, 0, 0)` }"
        />
        <div
          class="absolute top-0 h-full w-0.5 bg-n-iris-9 will-change-transform"
          data-testid="workbench-list-drag-indicator"
          :style="{
            transform: `translate3d(${listDragPreviewPx || 0}px, 0, 0)`,
          }"
        />
      </div>
      <ConversationBox
        v-if="showMessageView"
        :inbox-id="inboxId"
        :conversation-id="conversationId"
        :is-on-expanded-layout="isOnSmallScreen"
      />
      <WorkspaceContactToggle
        v-if="isWorkspaceV2 && !isOnSmallScreen && hasCurrentChat"
      />
      <div
        v-else-if="!isOnSmallScreen && hasCurrentChat"
        data-testid="conversation-workbench-right-gutter"
        class="hidden md:block relative w-2 shrink-0 h-full cursor-col-resize group"
        @mousedown="startRightDrag"
        @touchstart="startRightDrag"
      >
        <div
          class="absolute inset-y-0 w-px bg-transparent group-hover:bg-n-brand transition-colors"
        />
      </div>
      <ConversationSidebar v-if="hasCurrentChat" :current-chat="currentChat" />
      <SidepanelSwitch v-if="currentChat.id" />
      <CmdBarConversationSnooze />
    </section>
  </section>
</template>

<style scoped>
/* Fecha a lista no grid (0fr), não na width do ChatList — o Virtualizer
   não recalcula item a cada frame. Mesma receita da nav no Dashboard. */
.workbench-list-rail {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  transition: grid-template-columns var(--motion-duration-layout)
    var(--motion-ease-spring);
}
.workbench-list-rail--closed {
  grid-template-columns: minmax(0, 0fr);
  pointer-events: none;
}
.workbench-list-rail--dragging {
  transition: none;
}
.workbench-list-rail__inner {
  display: flex;
  min-width: 0;
  overflow: hidden;
  height: 100%;
}
</style>
